import { useCallback, useEffect, useRef } from 'react';

/**
 * Overlay thumb whose position follows the pointer while dragging.
 * Native webkit thumb-drag updates content on the main thread and trails
 * the mouse; this writes scrollTop and the thumb transform from the same move.
 */
export function useCampaignsScrollThumb() {
  const scrollRef = useRef(null);
  const trackRef = useRef(null);
  const thumbRef = useRef(null);
  const dragRef = useRef(null);

  const layout = useCallback(() => {
    const el = scrollRef.current;
    const track = trackRef.current;
    const thumb = thumbRef.current;
    if (!el || !track || !thumb) return null;

    const overflow = el.scrollHeight - el.clientHeight;
    if (overflow <= 1) {
      track.classList.add('sidebar__scroll-thumb-track--hidden');
      return null;
    }

    track.classList.remove('sidebar__scroll-thumb-track--hidden');
    const trackH = track.clientHeight;
    const thumbH = Math.max(28, (el.clientHeight / el.scrollHeight) * trackH);
    const maxTop = Math.max(0, trackH - thumbH);
    return { el, track, thumb, overflow, thumbH, maxTop };
  }, []);

  const placeThumb = useCallback((top, thumbH) => {
    const thumb = thumbRef.current;
    if (!thumb) return;
    thumb.style.height = `${thumbH}px`;
    thumb.style.transform = `translate3d(0, ${top}px, 0)`;
  }, []);

  const syncFromScroll = useCallback(() => {
    if (dragRef.current) return;
    const metrics = layout();
    if (!metrics) return;
    const top = metrics.maxTop === 0
      ? 0
      : (metrics.el.scrollTop / metrics.overflow) * metrics.maxTop;
    placeThumb(top, metrics.thumbH);
  }, [layout, placeThumb]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;

    el.addEventListener('scroll', syncFromScroll, { passive: true });
    const ro = new ResizeObserver(syncFromScroll);
    ro.observe(el);
    if (el.firstElementChild) ro.observe(el.firstElementChild);
    syncFromScroll();

    return () => {
      el.removeEventListener('scroll', syncFromScroll);
      ro.disconnect();
    };
  }, [syncFromScroll]);

  const onPointerDown = useCallback((event) => {
    if (event.button !== 0) return;
    const metrics = layout();
    if (!metrics) return;

    const trackRect = metrics.track.getBoundingClientRect();
    const thumbRect = metrics.thumb.getBoundingClientRect();
    const onThumb = metrics.thumb.contains(event.target);
    let top = thumbRect.top - trackRect.top;
    if (!onThumb) {
      top = Math.min(
        metrics.maxTop,
        Math.max(0, event.clientY - trackRect.top - metrics.thumbH / 2)
      );
    }

    placeThumb(top, metrics.thumbH);
    metrics.el.scrollTop = metrics.maxTop === 0 ? 0 : (top / metrics.maxTop) * metrics.overflow;

    dragRef.current = {
      pointerId: event.pointerId,
      startY: event.clientY,
      startTop: top,
      maxTop: metrics.maxTop,
      overflow: metrics.overflow,
      thumbH: metrics.thumbH,
    };
    metrics.track.setPointerCapture(event.pointerId);
    event.preventDefault();
  }, [layout, placeThumb]);

  const onPointerMove = useCallback((event) => {
    const drag = dragRef.current;
    const el = scrollRef.current;
    if (!drag || !el || event.pointerId !== drag.pointerId) return;

    const top = Math.min(
      drag.maxTop,
      Math.max(0, drag.startTop + (event.clientY - drag.startY))
    );
    placeThumb(top, drag.thumbH);
    el.scrollTop = drag.maxTop === 0 ? 0 : (top / drag.maxTop) * drag.overflow;
  }, [placeThumb]);

  const onPointerUp = useCallback((event) => {
    const drag = dragRef.current;
    const track = trackRef.current;
    if (!drag || event.pointerId !== drag.pointerId) return;
    dragRef.current = null;
    if (track?.hasPointerCapture(event.pointerId)) {
      track.releasePointerCapture(event.pointerId);
    }
  }, []);

  return {
    scrollRef,
    trackRef,
    thumbRef,
    onPointerDown,
    onPointerMove,
    onPointerUp,
  };
}
